/* eslint-disable consistent-return */
import {
  Alert,
  Badge,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Grid,
  Header,
  Icon,
  Input,
  Link,
  RadioGroup,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TokenGroup,
} from '@amzn/awsui-components-react-v3';
import _ from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import KeyValue from '../../../components/KeyValue';
import { Category, ContainerBlock, ContainerDefinitionResults, ContainerRule } from '../types';
import './style.css';
import {
  ContainerDefinition,
  GetContainerResponse,
} from '../../../services/container-management/types';
import {
  getContainerBlock,
  getFQContainerName,
  getRulesList,
  getRulesOptions,
  isContainerDefinitionMovable,
  transformPoliciesToRules,
} from './helper';
import { ContainerUpdateConfirmationModal } from './ContainerUpdateConfirmationModal';
import { isObjectDefined, validateInput } from '../../../utils';
import {
  GET_CMS_CONTAINER,
  GET_LIST_CONTAINER_DEFINITION,
  GET_POLICIES,
} from '../../../services/container-management/queries';
import {
  getCategoryOptions,
  getSubcategoryOptions,
  parseListContainerDefinitionsResponse,
} from '../../../utils/ContainerManagement/containerDefinitionsHelper';
import { zoneFeatureFlag } from '../../../utils/featureFlags';

export type ContainerDetailProps = {
  getContainerResponse: GetContainerResponse;
  rules: Map<ContainerBlock, ContainerRule>;
  isContainerDeletable: boolean;
  isContainerEditable: boolean;
  sizeOptions: OptionDefinition[];
  sizeReasonCodes: OptionDefinition[];
  sealIdReasonCodes: OptionDefinition[];
  rulesReasonCodes: OptionDefinition[];
};

export const ContainerDetail = ({
  data,
  siteName,
  fqname,
  managePermissions,
}: {
  data: ContainerDetailProps;
  siteName: string;
  fqname: string;
  managePermissions: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCategorySubCategoryEditable, setIsCategorySubCategoryEditable] = useState(false);
  const [showContainerUpdateConfirmModal, setShowContainerUpdateConfirmModal] = useState(false);
  const [newVersion, setNewVersion] = useState(data.getContainerResponse.container.version);
  const [newSection, setNewSection] = useState(
    data.getContainerResponse.container.metadata?.section?.toUpperCase() ?? ''
  );
  const [newAisle, setNewAisle] = useState(
    data.getContainerResponse.container.metadata?.aisle?.toUpperCase() ?? ''
  );
  const [newLevel, setNewLevel] = useState(
    data.getContainerResponse.container.metadata?.level?.toUpperCase() ?? ''
  );
  const [newCell, setNewCell] = useState(
    data.getContainerResponse.container.containerName.toUpperCase()
  );
  const [newAlphabetIdentifier, setNewAlphabetIdentifier] = useState(
    data.getContainerResponse.container.metadata?.alphabetIdentifier?.toUpperCase() ?? ''
  );
  const [isCompliant] = useState<boolean>(
    data.getContainerResponse.container.metadata?.isCompliant
  );
  const [newState, setNewState] = useState(data.getContainerResponse.container.state);
  const originalRulesMap: Map<ContainerBlock, ContainerRule> = _.cloneDeep(data.rules);
  const [newRulesMap, setNewRulesMap] = useState(originalRulesMap);
  const [newRulesList, setNewRulesList] = useState(getRulesList(Array.from(newRulesMap.values())));
  const [rulesOptions, setRulesOptions] = useState(
    getRulesOptions(Array.from(newRulesMap.values()))
  );
  const [areRulesUpdated, setAreRulesUpdated] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [sectionError, setSectionError] = useState<string>('');
  const [aisleError, setAisleError] = useState<string>('');
  const [levelError, setLevelError] = useState<string>('');
  const [cellError, setCellError] = useState<string>('');
  const [alphabetIdentifierError, setAlphabetIdentifierError] = useState<string>('');
  const [alphabetIdentifierErrorColor, setAlphabetIdentifierErrorColor] = useState<string>('');
  const [containerDefinitionsMap, setContainerDefinitionsMap] = useState<Map<
    string,
    ContainerDefinition
  > | null>(null);
  const [containerDefinitionToPolicies, setContainerDefinitionToPolicies] = useState<Map<
    string,
    string[]
  > | null>(null);
  const [containerType] = useState<string>(
    data.getContainerResponse.containerDefinition.containerType.toLocaleLowerCase()
  );
  const [typeToContainerDefinitionResults, setTypeToContainerDefinitionResults] = useState<
    Map<string, ContainerDefinitionResults>
  >(new Map());
  const [newCategory, setNewCategory] = useState<SelectProps.Option | null>({
    label: data.getContainerResponse?.containerDefinition?.category,
    value: data.getContainerResponse?.containerDefinition?.category,
  });
  const [newSubcategory, setNewSubcategory] = useState<SelectProps.Option | null>({
    label: data.getContainerResponse?.containerDefinition?.subcategory,
    value: data.getContainerResponse?.containerDefinition?.subcategory,
  });
  const [categoryOptions, setCategoryOptions] = useState<any>([]);
  const [categoriesMap, setCategoriesMap] = useState<Map<string, Category> | null>(null);
  const [newContainerDefinition, setNewContainerDefinition] = useState<any>(
    data.getContainerResponse.containerDefinition
  );
  const [isRulesDisabled, setIsRulesDisabled] = useState(false);
  const [parentContainerId, setParentContainerId] = useState<string>('');
  const [parentContainerName, setParentContainerName] = useState<string>('');
  const [custodyUser, setCustodyUser] = useState<string>('');

  let containerNamePrefix =
    data.getContainerResponse.containerDefinition.metadataConfiguration.containerNamePrefix
      ?.default;

  const isContainerMovable = isContainerDefinitionMovable(
    data.getContainerResponse.containerDefinition
  );

  const [
    getParentContainer,
    { loading: getParentContainerLoading, error: getParentContainerError },
  ] = useLazyQuery(GET_CMS_CONTAINER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const response: GetContainerResponse = result.getCmsContainer;
      setParentContainerName(
        getFQContainerName(
          response?.container,
          response?.containerDefinition,
          siteName,
          response?.location?.locationName
        )
      );
    },
  });

  const [getContainerVersion] = useLazyQuery(GET_CMS_CONTAINER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const response: GetContainerResponse = result.getCmsContainer;
      setNewVersion(response.container.version);
    },
  });

  useEffect(() => {
    if (
      isObjectDefined(data?.getContainerResponse?.container?.metadata?.isCompliant) &&
      !isCompliant
    ) {
      if (isContainerMovable) {
        setAlertText(`Please print the label for this container`);
      } else {
        setAlertText(
          `This container is non-compliant. Please update the Container Details below to make it Compliant.`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const parentLocations = data?.getContainerResponse?.container?.parentlocation;
    if (parentLocations) {
      parentLocations.forEach((loc) => {
        if (loc.type === 'CONTAINER') {
          getParentContainer({
            variables: {
              containerId: loc.value,
            },
          });
          setParentContainerId(loc.value);
        } else if (loc.type === 'CUSTODY') {
          setCustodyUser(loc.value);
        }
      });
    }

    if (
      data.getContainerResponse.containerDefinition.category === 'Default' &&
      data.getContainerResponse.containerDefinition.subcategory === 'Default'
    ) {
      setIsCategorySubCategoryEditable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setSectionError(validateInput(newSection ?? '', false, true));
  }, [newSection]);

  useEffect(() => {
    setAisleError(validateInput(newAisle ?? '', false, true));
  }, [newAisle]);

  useEffect(() => {
    setLevelError(validateInput(newLevel ?? '', false, true));
  }, [newLevel]);

  useEffect(() => {
    let newCellError = validateInput(newCell, true, true);
    if (newSubcategory && newSubcategory.value) {
      const namePrefix = containerDefinitionsMap?.get(newSubcategory?.value)?.metadataConfiguration
        .containerNamePrefix?.default;
      if (!newCellError && namePrefix && !newCell.startsWith(namePrefix)) {
        newCellError = `Container name must begin with prefix ${namePrefix}`;
      }
    }
    setCellError(newCellError);
  }, [containerDefinitionsMap, containerNamePrefix, newCell, newSubcategory]);

  useEffect(() => {
    const recommendedAlphabetIdentifier =
      data.getContainerResponse.containerDefinition.metadataConfiguration?.alphabetIdentifier
        ?.default;
    let newAlphabetIdentifierError = validateInput(newAlphabetIdentifier ?? '', false, true);
    let newAlphabetIdentifierErrorColor = 'red';
    if (!newAlphabetIdentifierError && newAlphabetIdentifier !== recommendedAlphabetIdentifier) {
      newAlphabetIdentifierError = `Recommended Alphabet Identifier is ${recommendedAlphabetIdentifier}`;
      newAlphabetIdentifierErrorColor = 'orange';
    }

    setAlphabetIdentifierError(newAlphabetIdentifierError);
    setAlphabetIdentifierErrorColor(newAlphabetIdentifierErrorColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAlphabetIdentifier]);

  const [
    listContainerDefinitions,
    { loading: listContainerDefinitionsLoading, error: listContainerDefinitionsError },
  ] = useLazyQuery(GET_LIST_CONTAINER_DEFINITION, {
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const [categoryMapResult, containerDefinitionMapResult, containerDefinitionToPolicyIds] =
        parseListContainerDefinitionsResponse(
          result.listContainerDefinitions.containerType,
          result.listContainerDefinitions.categories
        );
      setTypeToContainerDefinitionResults(
        _.cloneDeep(
          typeToContainerDefinitionResults.set(result.listContainerDefinitions.containerType, {
            categoryMap: categoryMapResult,
            containerDefinitionMap: containerDefinitionMapResult,
          })
        )
      );
      setContainerDefinitionToPolicies(containerDefinitionToPolicyIds);
      setCategoryOptions(
        getCategoryOptions(typeToContainerDefinitionResults.get(containerType)?.categoryMap ?? null)
      );
      setCategoriesMap(typeToContainerDefinitionResults.get(containerType)?.categoryMap ?? null);
      setContainerDefinitionsMap(
        typeToContainerDefinitionResults.get(containerType)?.containerDefinitionMap ?? null
      );
    },
  });

  const [callGetPolicies, { loading: getPolicyLoading, error: getPolicyError }] = useLazyQuery(
    GET_POLICIES,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        const newRules = transformPoliciesToRules(result.getPolicies.policies);
        setNewRulesMap(newRules);
        setNewRulesList(getRulesList(Array.from(newRules.values())));
      },
    }
  );

  const handleEdit = (flag: boolean): any => {
    if (flag) {
      getContainerVersion({
        variables: {
          containerId: data.getContainerResponse.container.containerId,
        },
      });
    }
    setIsEditMode(flag);
    if (newCategory?.label === 'Default') {
      setNewCategory(null);
    }
    if (newSubcategory?.label === 'Default') {
      setNewSubcategory(null);
    }
    if (categoriesMap === null) {
      listContainerDefinitions({
        variables: {
          containerType: data.getContainerResponse.containerDefinition.containerType.toLowerCase(),
        },
      });
    }
  };
  const getHeader = () =>
    !isEditMode ? (
      <Header
        variant="h2"
        description=""
        actions={
          data.isContainerEditable && (
            <Button
              data-test-id="edit"
              variant="normal"
              disabled={!managePermissions}
              onClick={() => handleEdit(true)}
            >
              {t('Edit')}
            </Button>
          )
        }
      >
        {t('Container Details')}
      </Header>
    ) : (
      <Header
        data-test-id="header"
        variant="h2"
        description=""
        actions={
          <SpaceBetween direction="horizontal" size="s">
            <Button
              data-test-id="cancel"
              variant="normal"
              onClick={() => {
                if (!isContainerDefinitionMovable(data.getContainerResponse.containerDefinition)) {
                  setNewSection(
                    data.getContainerResponse.container.metadata?.section?.toUpperCase() ?? ''
                  );
                  setNewAisle(
                    data.getContainerResponse.container.metadata?.aisle?.toUpperCase() ?? ''
                  );
                  setNewLevel(
                    data.getContainerResponse.container.metadata?.level?.toUpperCase() ?? ''
                  );
                  setNewCell(data.getContainerResponse.container.containerName.toUpperCase());
                  setNewAlphabetIdentifier(
                    data.getContainerResponse.container.metadata?.alphabetIdentifier?.toUpperCase() ??
                      ''
                  );
                }
                setNewState(data.getContainerResponse.container.state);
                setNewRulesMap(originalRulesMap);
                setNewRulesList(getRulesList(Array.from(originalRulesMap.values())));
                setRulesOptions(getRulesOptions(Array.from(originalRulesMap.values())));
                setIsEditMode(false);
                setAreRulesUpdated(false);
                setNewSubcategory({
                  label: data.getContainerResponse?.containerDefinition?.subcategory,
                  value: data.getContainerResponse?.container?.containerDefinitionId,
                });
                setNewCategory({
                  label: data.getContainerResponse?.containerDefinition?.category,
                  value: data.getContainerResponse?.containerDefinition?.category,
                });
                setNewContainerDefinition(data.getContainerResponse?.containerDefinition);
                setIsRulesDisabled(false);
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              data-test-id="save"
              variant="primary"
              disabled={isSaveChangesDisabled()}
              onClick={() => setShowContainerUpdateConfirmModal(true)}
            >
              {t('Save Changes')}
            </Button>
          </SpaceBetween>
        }
      >
        {t('Container Details')}
      </Header>
    );

  const isSaveChangesDisabled = () => {
    const nameChanged = isContainerMovable
      ? false
      : newAlphabetIdentifier !==
          (data.getContainerResponse.container.metadata?.alphabetIdentifier ?? '') ||
        newSection !== (data.getContainerResponse.container.metadata?.section ?? '') ||
        newAisle !== (data.getContainerResponse.container.metadata?.aisle ?? '') ||
        newLevel !== (data.getContainerResponse.container.metadata?.level ?? '') ||
        newCell !== data.getContainerResponse.container.containerName;
    return (
      newCategory === null ||
      newSubcategory === null ||
      // for non-movable containers, disable if category/sub-category are selected and there are no name errors
      (!isContainerMovable &&
        (sectionError.length > 0 ||
          aisleError.length > 0 ||
          levelError.length > 0 ||
          cellError.length > 0 ||
          (alphabetIdentifierError.length > 0 && alphabetIdentifierErrorColor === 'red'))) ||
      // for all containers, disable if there has been no change
      !(
        newState !== data.getContainerResponse.container.state ||
        newCategory?.label !== data.getContainerResponse.containerDefinition.category ||
        newSubcategory?.label !== data.getContainerResponse.containerDefinition.subcategory ||
        nameChanged ||
        areRulesUpdated
      )
    );
  };

  const getTypeSection = () => (
    <div className={isEditMode ? 'grayout' : ''}>
      <KeyValue label="Container Type" fontWeight="bold">
        {t(`container-management.${data.getContainerResponse.containerDefinition.containerType}`)}
      </KeyValue>
    </div>
  );

  const getCategorySection = () => {
    if (isEditMode && isCategorySubCategoryEditable) {
      return (
        <FormField label={t('Category')} errorText={!newCategory}>
          <Select
            data-test-id="categoryDropdown"
            selectedOption={newCategory}
            onChange={({ detail }) => {
              setNewCategory(detail.selectedOption);
              setNewSubcategory(null);
            }}
            options={categoryOptions}
            statusType={
              // eslint-disable-next-line no-nested-ternary
              listContainerDefinitionsLoading
                ? 'loading'
                : listContainerDefinitionsError
                ? 'error'
                : 'finished'
            }
            disabled={!(newState.toLowerCase() === 'active')}
          />
        </FormField>
      );
    }
    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label="Category" fontWeight="bold">
          {data.getContainerResponse.containerDefinition.category}
        </KeyValue>
      </div>
    );
  };

  const getSubcategorySection = () => {
    if (isEditMode && isCategorySubCategoryEditable) {
      return (
        <FormField label={t('Subcategory')} errorText={!newSubcategory}>
          <Select
            data-test-id="subcategoryDropdown"
            selectedOption={newSubcategory}
            onChange={({ detail }) => {
              setNewSubcategory(detail.selectedOption);
              setNewContainerDefinition(
                containerDefinitionsMap?.get(detail.selectedOption.value || '')
              );
              if (detail.selectedOption.value != null) {
                const containerDef = containerDefinitionsMap?.get(detail.selectedOption.value);
                const getPoliciesResponse = containerDefinitionToPolicies?.get(
                  detail.selectedOption.value
                );
                if (
                  data.getContainerResponse?.containerDefinition?.category === newCategory?.label &&
                  data.getContainerResponse?.containerDefinition?.subcategory ===
                    detail.selectedOption.label
                ) {
                  setNewRulesMap(originalRulesMap);
                  setNewRulesList(getRulesList(Array.from(originalRulesMap.values())));
                  setRulesOptions(getRulesOptions(Array.from(originalRulesMap.values())));
                  setIsRulesDisabled(false);
                } else {
                  callGetPolicies({ variables: { policyIds: getPoliciesResponse } });
                  setIsRulesDisabled(true);
                }
                containerNamePrefix =
                  containerDef?.metadataConfiguration.containerNamePrefix?.default;
                // Set a fallback size for until corresponding GQL change is available
                if (!isContainerDefinitionMovable(data.getContainerResponse.containerDefinition)) {
                  if (containerNamePrefix) {
                    let nameBuilder = '';
                    if (newAlphabetIdentifier)
                      nameBuilder = nameBuilder.concat(newAlphabetIdentifier);
                    if (newSection) nameBuilder = nameBuilder.concat(newSection);
                    if (newAisle) nameBuilder = nameBuilder.concat(newAisle);
                    if (newLevel) nameBuilder = nameBuilder.concat(newLevel);
                    nameBuilder = nameBuilder.concat(newCell);
                    setNewCell(nameBuilder);
                    setNewSection('');
                    setNewAlphabetIdentifier('');
                    setNewAisle('');
                    setNewLevel('');
                  } else if (
                    data.getContainerResponse.containerDefinition?.category === 'Default' &&
                    data.getContainerResponse.containerDefinition?.subcategory === 'Default' &&
                    data.getContainerResponse.container.containerName.length > 6 &&
                    !containerDef?.metadataConfiguration.virtualBinType?.default
                  ) {
                    setNewAlphabetIdentifier(
                      data.getContainerResponse.container.containerName
                        .substring(0, 1)
                        ?.toUpperCase() ?? ''
                    );
                    setNewSection(
                      data.getContainerResponse.container.containerName
                        .substring(1, 2)
                        ?.toUpperCase() ?? ''
                    );
                    setNewAisle(
                      data.getContainerResponse.container.containerName
                        .substring(2, 4)
                        ?.toUpperCase() ?? ''
                    );
                    setNewLevel(
                      data.getContainerResponse.container.containerName
                        .substring(4, 6)
                        ?.toUpperCase() ?? ''
                    );
                    setNewCell(
                      data.getContainerResponse.container.containerName
                        .substring(6, data.getContainerResponse.container.containerName.length)
                        ?.toUpperCase() ?? ''
                    );
                  } else {
                    setNewCell(data.getContainerResponse.container.containerName.toUpperCase());
                    setNewSection(
                      data.getContainerResponse.container.metadata?.section?.toUpperCase() ?? ''
                    );
                    setNewAlphabetIdentifier(
                      data.getContainerResponse.container.metadata?.alphabetIdentifier?.toUpperCase() ??
                        ''
                    );
                    setNewAisle(
                      data.getContainerResponse.container.metadata?.aisle?.toUpperCase() ?? ''
                    );
                    setNewLevel(
                      data.getContainerResponse.container.metadata?.level?.toUpperCase() ?? ''
                    );
                  }
                }
              }
            }}
            options={getSubcategoryOptions(categoriesMap, newCategory)}
            disabled={!(newState.toLowerCase() === 'active')}
          />
        </FormField>
      );
    }
    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label="Subcategory" fontWeight="bold">
          {data.getContainerResponse.containerDefinition.subcategory}
        </KeyValue>
      </div>
    );
  };

  const getSectionSection = () => {
    if (!isEditMode) {
      return (
        <KeyValue label={t('Section')} fontWeight="bold">
          {data.getContainerResponse.container.metadata?.section || t('N/A')}
        </KeyValue>
      );
    }
    return newContainerDefinition == null ||
      isObjectDefined(newContainerDefinition?.metadataConfiguration?.section?.default) ||
      (!newContainerDefinition &&
        data.getContainerResponse.containerDefinition.metadataConfiguration?.section?.default) ? (
      <div className={newState.toLowerCase() === 'active' ? '' : 'grayout'}>
        <KeyValue label={t('Section')} fontWeight="bold">
          <SpaceBetween direction="vertical" size="xs">
            <Input
              data-test-id="sectionInput"
              value={newSection || ''}
              onChange={({ detail }) => {
                setNewSection(detail.value.toUpperCase());
              }}
              disabled={!(newState.toLowerCase() === 'active')}
            />
            {sectionError && (
              <small
                style={{
                  color: 'red',
                }}
              >
                {sectionError}
              </small>
            )}
          </SpaceBetween>
        </KeyValue>
      </div>
    ) : (
      <div className="grayout">
        <KeyValue label={t('Section')} fontWeight="bold">
          {!newContainerDefinition
            ? data.getContainerResponse.container.metadata?.section
            : newContainerDefinition?.metadataConfiguration?.section?.default}
        </KeyValue>
      </div>
    );
  };

  const getAisleSection = () => {
    if (!isEditMode) {
      return (
        <KeyValue label={t('Aisle')} fontWeight="bold">
          {data.getContainerResponse.container.metadata?.aisle || t('N/A')}
        </KeyValue>
      );
    }
    return newContainerDefinition == null ||
      isObjectDefined(newContainerDefinition?.metadataConfiguration?.aisle?.default) ||
      (!newContainerDefinition &&
        data.getContainerResponse.containerDefinition.metadataConfiguration?.aisle?.default) ? (
      <div className={newState.toLowerCase() === 'active' ? '' : 'grayout'}>
        <KeyValue label={t('Aisle')} fontWeight="bold">
          <SpaceBetween direction="vertical" size="xs">
            <Input
              data-test-id="aisleInput"
              value={newAisle || ''}
              onChange={({ detail }) => setNewAisle(detail.value.toUpperCase())}
              disabled={!(newState.toLowerCase() === 'active')}
            />
            {aisleError && (
              <small
                style={{
                  color: 'red',
                }}
              >
                {aisleError}
              </small>
            )}
          </SpaceBetween>
        </KeyValue>
      </div>
    ) : (
      <div className="grayout">
        <KeyValue label={t('Aisle')} fontWeight="bold">
          {!newContainerDefinition
            ? data.getContainerResponse.container.metadata?.aisle
            : newContainerDefinition?.metadataConfiguration?.aisle?.default}
        </KeyValue>
      </div>
    );
  };

  const getLevelSection = () => {
    if (!isEditMode) {
      return (
        <KeyValue label={t('Level')} fontWeight="bold">
          {data.getContainerResponse.container.metadata?.level || t('N/A')}
        </KeyValue>
      );
    }
    return newContainerDefinition == null ||
      isObjectDefined(newContainerDefinition?.metadataConfiguration?.level?.default) ||
      (!newContainerDefinition &&
        data.getContainerResponse.containerDefinition.metadataConfiguration?.level?.default) ? (
      <div className={newState.toLowerCase() === 'active' ? '' : 'grayout'}>
        <KeyValue label={t('Level')} fontWeight="bold">
          <SpaceBetween direction="vertical" size="xs">
            <Input
              data-test-id="levelInput"
              value={newLevel || ''}
              onChange={({ detail }) => setNewLevel(detail.value.toUpperCase())}
              disabled={!(newState.toLowerCase() === 'active')}
            />
            {levelError && (
              <small
                style={{
                  color: 'red',
                }}
              >
                {levelError}
              </small>
            )}
          </SpaceBetween>
        </KeyValue>
      </div>
    ) : (
      <div className="grayout">
        <KeyValue label={t('Level')} fontWeight="bold">
          {!newContainerDefinition
            ? data.getContainerResponse.container.metadata?.level
            : newContainerDefinition?.metadataConfiguration?.level?.default}
        </KeyValue>
      </div>
    );
  };

  const getCellSection = () => {
    if (!isEditMode) {
      return (
        <KeyValue label={t('Cell')} fontWeight="bold">
          {data.getContainerResponse.container.containerName}
        </KeyValue>
      );
    }
    return (
      <div className={newState.toLowerCase() === 'active' ? '' : 'grayout'}>
        <KeyValue label={t('Cell')} fontWeight="bold">
          <SpaceBetween direction="vertical" size="xs">
            <Input
              data-test-id="cellInput"
              value={newCell || ''}
              onChange={({ detail }) => setNewCell(detail.value.toUpperCase())}
              disabled={!(newState.toLowerCase() === 'active')}
            />
            {cellError && (
              <small
                data-test-id="cellError"
                style={{
                  color: 'red',
                }}
              >
                {cellError}
              </small>
            )}
          </SpaceBetween>
        </KeyValue>
      </div>
    );
  };

  const getAlphabetIdentifierSection = () => {
    if (!isEditMode) {
      return (
        <KeyValue label={t('Alphabet Identifier')} fontWeight="bold">
          {data.getContainerResponse.container.metadata?.alphabetIdentifier || t('N/A')}
        </KeyValue>
      );
    }
    return isObjectDefined(
      newContainerDefinition?.metadataConfiguration?.alphabetIdentifier?.default
    ) ||
      (!newContainerDefinition &&
        data.getContainerResponse.containerDefinition.metadataConfiguration?.alphabetIdentifier
          ?.default) ? (
      <div className={newState.toLowerCase() === 'active' ? '' : 'grayout'}>
        <KeyValue label={t('Alphabet Identifier')} fontWeight="bold">
          <SpaceBetween direction="vertical" size="xs">
            <Input
              data-test-id="alphabetIdentifierInput"
              value={newAlphabetIdentifier || ''}
              onChange={({ detail }) => setNewAlphabetIdentifier(detail.value.toUpperCase())}
              disabled={!(newState.toLowerCase() === 'active')}
            />
            {alphabetIdentifierError && (
              <small
                style={{
                  color: alphabetIdentifierErrorColor,
                }}
              >
                {alphabetIdentifierError}
              </small>
            )}
          </SpaceBetween>
        </KeyValue>
      </div>
    ) : (
      <div className="grayout">
        <KeyValue
          data-test-id="alphabetIdentifierText"
          label={t('Alphabet Identifier')}
          fontWeight="bold"
        >
          {!newContainerDefinition
            ? data.getContainerResponse.container.metadata?.alphabetIdentifier
            : newContainerDefinition?.metadataConfiguration?.alphabetIdentifier?.default}
        </KeyValue>
      </div>
    );
  };

  const getRoomSection = () => (
    <div className={isEditMode ? 'grayout' : ''}>
      <KeyValue label={t('Room')} fontWeight="bold">
        {data.getContainerResponse.location?.locationName || t('N/A')}
      </KeyValue>
    </div>
  );

  const getZoneSection = () =>
    zoneFeatureFlag() && (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={t('Zone')} fontWeight="bold">
          {data.getContainerResponse.location?.zone || t('N/A')}
        </KeyValue>
      </div>
    );

  const getSiteSection = () => (
    <div className={isEditMode ? 'grayout' : ''}>
      <KeyValue label={t('Site')} fontWeight="bold">
        {siteName || t('N/A')}
      </KeyValue>
    </div>
  );

  const getClusterSection = () =>
    zoneFeatureFlag() && (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={t('Cluster')} fontWeight="bold">
          {siteName ? siteName.substring(0, 3) : t('N/A')}
        </KeyValue>
      </div>
    );

  const getCCTVSection = () => {
    const cctvRequired = newContainerDefinition
      ? newContainerDefinition?.metadataConfiguration?.cctvRequired?.default
      : data.getContainerResponse.containerDefinition.metadataConfiguration?.cctvRequired?.default;

    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={t('CCTV Coverage Required')} fontWeight="bold">
          {isObjectDefined(cctvRequired) && cctvRequired ? 'Yes' : 'No'}
        </KeyValue>
      </div>
    );
  };

  const getRulesSection = () => {
    if (isContainerDefinitionIVTAVTType()) {
      return (
        <div className={!isEditMode ? '' : 'grayout'}>
          <KeyValue label={t('Rules')} fontWeight="bold">
            <p>{t('N/A')}</p>
          </KeyValue>
        </div>
      );
    }
    const ruleItems = Array.from(data.rules.values())
      .filter((cmsRule) => cmsRule.enabled)
      .map((cmsRule) => (
        <Badge color="grey" key={cmsRule.rule}>
          {cmsRule.rule}
        </Badge>
      ));
    if (!isEditMode) {
      return (
        <KeyValue label={t('Rules')} fontWeight="bold">
          {ruleItems.length > 0 ? <Grid>{ruleItems}</Grid> : <p>{t('N/A')}</p>}
        </KeyValue>
      );
    }
    const canEditRules =
      data.getContainerResponse.containerDefinition.metadataConfiguration.virtualBinType === null &&
      !isContainerDefinitionIVTAVTType();
    return canEditRules ? (
      <div className={newState.toLowerCase() === 'active' ? '' : 'grayout'}>
        <KeyValue label={t('Rules')} fontWeight="bold">
          <>
            <Select
              data-test-id="rulesDropdown"
              selectedOption={null}
              onChange={({ detail }) => {
                if (detail.selectedOption?.value) {
                  const block = getContainerBlock(detail.selectedOption.value);
                  if (block) {
                    newRulesMap.set(block, {
                      enabled: true,
                      rule: block,
                      isOverride: !newRulesMap.get(block)?.isOverride,
                      policyId: null,
                    });
                    setNewRulesMap(newRulesMap);
                    setNewRulesList(getRulesList(Array.from(newRulesMap.values())));
                    setRulesOptions(getRulesOptions(Array.from(newRulesMap.values())));
                    setAreRulesUpdated(!_.isEqual(newRulesMap, originalRulesMap));
                  }
                }
              }}
              options={rulesOptions}
              statusType={getPolicyError ? 'error' : 'finished'}
              placeholder="Add a rule"
              disabled={!(newState.toLowerCase() === 'active') || isRulesDisabled}
            />
            {getPolicyLoading && <Spinner size="normal" />}
            <div className={!isRulesDisabled ? '' : 'grayout'}>
              <TokenGroup
                data-test-id="rulesTokenGroup"
                onDismiss={({ detail: { itemIndex } }) => {
                  if (newState.toLowerCase() === 'active') {
                    const dismissedBlock = getContainerBlock(newRulesList[itemIndex].label);
                    if (dismissedBlock) {
                      const originalRule = newRulesMap.get(dismissedBlock);
                      if (originalRule) {
                        newRulesMap.set(dismissedBlock, {
                          enabled: false,
                          rule: dismissedBlock,
                          isOverride: !originalRule.isOverride,
                          policyId: null,
                        });
                        setNewRulesMap(newRulesMap);
                        setNewRulesList([
                          ...newRulesList.slice(0, itemIndex),
                          ...newRulesList.slice(itemIndex + 1),
                        ]);
                        setRulesOptions(getRulesOptions(Array.from(newRulesMap.values())));
                        setAreRulesUpdated(!_.isEqual(newRulesMap, originalRulesMap));
                      }
                    }
                  }
                }}
                items={newRulesList}
              />
            </div>
          </>
        </KeyValue>
      </div>
    ) : (
      <div className="grayout">
        <KeyValue label={t('Rules')} fontWeight="bold">
          {ruleItems.length > 0 ? <Grid>{ruleItems}</Grid> : <p>{t('N/A')}</p>}
        </KeyValue>
      </div>
    );
  };

  const getOverriddenRulesSection = () => {
    if (isContainerDefinitionIVTAVTType()) {
      return (
        <div className={!isEditMode ? '' : 'grayout'}>
          <KeyValue label={t('Overridden Rules')} fontWeight="bold">
            <p>{t('N/A')}</p>
          </KeyValue>
        </div>
      );
    }
    /* TODO add conditional on user role */
    const ruleItems = Array.from(newRulesMap.values())
      .filter((cmsRule) => cmsRule.isOverride)
      .map((cmsRule) => (
        <Badge color={cmsRule.enabled ? 'green' : 'red'} key={cmsRule.rule}>
          {cmsRule.rule}
        </Badge>
      ));
    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={t('Overridden Rules')} fontWeight="bold">
          {ruleItems.length > 0 ? <Grid>{ruleItems}</Grid> : <p>{t('N/A')}</p>}
        </KeyValue>
      </div>
    );
  };

  // TODO: Allow users to ARCHIVE containers from detail page
  const getStateSection = () =>
    !isEditMode ? (
      <KeyValue label={t('State')} fontWeight="bold">
        {data.getContainerResponse.container.state}
      </KeyValue>
    ) : (
      <KeyValue label={t('State')} fontWeight="bold">
        <RadioGroup
          data-test-id="stateRadio"
          value={newState.toLowerCase()}
          items={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'archived', label: 'Archived' },
          ]}
          onChange={({ detail }) => {
            setNewState(detail.value);
          }}
        />
      </KeyValue>
    );

  const getComplianceSection = () => {
    let label;
    let content;

    if (isContainerMovable) {
      label = t('Is Label Printed');
      content = isCompliant ? <p>{t('Yes')}</p> : <p>{t('No')}</p>;
    } else {
      label = t('Compliance');
      content = isCompliant ? (
        <p>{t('Compliant')}</p>
      ) : (
        <SpaceBetween direction="horizontal" size="xs">
          {t('Not compliant')}
          <Icon variant="error" name="status-warning" />
        </SpaceBetween>
      );
    }
    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={label} fontWeight="bold">
          {content}
        </KeyValue>
      </div>
    );
  };

  const getParentContainerSection = () => {
    let content = <p>{t('N/A')}</p>;
    if (getParentContainerLoading) {
      content = <Spinner />;
    } else if (getParentContainerError) {
      content = (
        <StatusIndicator type="error">{t('Error getting parent container')}</StatusIndicator>
      );
    } else if (parentContainerId && parentContainerName) {
      content = (
        <Link href={`/assets-inventory/container/${parentContainerId}`}>{parentContainerName}</Link>
      );
    }
    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={t('Parent Container')} fontWeight="bold">
          {content}
        </KeyValue>
      </div>
    );
  };

  const getCustodySection = () => {
    return (
      <div className={isEditMode ? 'grayout' : ''}>
        <KeyValue label={t('User Custody')} fontWeight="bold">
          <p>{custodyUser || t('N/A')}</p>
        </KeyValue>
      </div>
    );
  };

  const isContainerDefinitionIVTAVTType = (): boolean =>
    data.getContainerResponse.containerDefinition.containerType.toLocaleLowerCase() === 'ivt' ||
    data.getContainerResponse.containerDefinition.containerType.toLocaleLowerCase() === 'avt';

  return (
    <SpaceBetween direction="vertical" size="m">
      {alertText && alertText.length > 0 && <Alert type="warning">{alertText}</Alert>}
      <Container header={getHeader()}>
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={4} variant="text-grid">
            {/* Row 1 */}
            {getTypeSection()}
            {getRoomSection()}
            {getRulesSection()}
            {isContainerDefinitionMovable(data.getContainerResponse.containerDefinition)
              ? getCustodySection()
              : getCategorySection()}

            {/* Row 2 */}
            {getStateSection()}
            {getSiteSection()}
            {getOverriddenRulesSection()}
            {isContainerDefinitionMovable(data.getContainerResponse.containerDefinition)
              ? getParentContainerSection()
              : getSubcategorySection()}

            {/* Row 3 */}
            {getZoneSection()}
            {getClusterSection()}

            {/* The rest of the rows depend on whether the fields exist */}
            {isObjectDefined(data?.getContainerResponse?.container?.metadata?.isCompliant) &&
              getComplianceSection()}
            {isObjectDefined(
              data.getContainerResponse.containerDefinition.metadataConfiguration?.cctvRequired
                ?.default
            ) && getCCTVSection()}
            {isObjectDefined(
              data.getContainerResponse.containerDefinition?.metadataConfiguration
                ?.alphabetIdentifier?.default
            ) && getAlphabetIdentifierSection()}
            {isObjectDefined(
              data.getContainerResponse.containerDefinition?.metadataConfiguration?.section?.default
            ) && getSectionSection()}
            {isObjectDefined(
              data.getContainerResponse.containerDefinition?.metadataConfiguration?.aisle?.default
            ) && getAisleSection()}
            {isObjectDefined(
              data.getContainerResponse.containerDefinition?.metadataConfiguration?.level?.default
            ) && getLevelSection()}

            {!isContainerDefinitionMovable(data.getContainerResponse.containerDefinition) &&
              getCellSection()}
          </ColumnLayout>
          {showContainerUpdateConfirmModal && (
            <ContainerUpdateConfirmationModal
              containerId={data.getContainerResponse.container.containerId}
              fqContainerName={fqname}
              version={newVersion}
              newSection={newSection || ''}
              newAisle={newAisle || ''}
              newLevel={newLevel || ''}
              newCell={newCell || ''}
              newAlphabetIdentifier={newAlphabetIdentifier || ''}
              newState={newState}
              newRules={newRulesMap}
              newContainerDefinitionId={newSubcategory?.value || ''}
              isSectionUpdated={
                newSection !== data.getContainerResponse.container.metadata?.section
              }
              isCategoryUpdated={
                newCategory?.label !== data.getContainerResponse.containerDefinition.category
              }
              isSubcategoryUpdated={
                newSubcategory?.label !== data.getContainerResponse.containerDefinition.subcategory
              }
              isAisleUpdated={newAisle !== data.getContainerResponse.container.metadata?.aisle}
              isLevelUpdated={newLevel !== data.getContainerResponse.container.metadata?.level}
              isCellUpdated={newCell !== data.getContainerResponse.container.containerName}
              isAlphabetIdentifierUpdated={
                newAlphabetIdentifier !==
                data.getContainerResponse.container.metadata?.alphabetIdentifier
              }
              isStateUpdated={newState !== data.getContainerResponse.container.state}
              areRulesUpdated={areRulesUpdated}
              originalRules={originalRulesMap}
              onClose={() => setShowContainerUpdateConfirmModal(false)}
              sizeReasonCodes={data.sizeReasonCodes}
              rulesReasonCodes={data.rulesReasonCodes}
            />
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
